/**
 * api/bulletins.ts —— 项目快报（06 §五 5.10）
 *
 * ⚠ 与 `api/assistant.ts` 同一处例外：`generate` 是 **SSE**，走裸 fetch；
 *   其余三个照常走 `client.ts` 的 `get/put/del`，**不要顺手也改成裸 fetch**。
 * ⚠ 本文件不做任何口径计算、不改字段名——字段真相在 `backend/src/types/bulletin.ts`。
 */
import type {
  Bulletin,
  BulletinPageView,
  BulletinPatchInput,
  BulletinPhoto,
  BulletinStreamEvent,
} from '@shared-types';
import { apiBase, del, get, post, put, type ReqOptions } from './client.js';

/**
 * 整页一次取齐。`no` 缺省时后端落到**最近一个已结束**的周期。
 * `id` = 查看该期某一份历史版本；不属于该期时后端照旧给最近一条。
 */
export const page = (no: number | null, id: string | null, opts?: ReqOptions): Promise<BulletinPageView> => {
  const q = new URLSearchParams();
  if (no != null) q.set('no', String(no));
  if (id) q.set('id', id);
  const s = q.toString();
  return get<BulletinPageView>(`/api/bulletins${s ? `?${s}` : ''}`, opts);
};

export interface PatchResult {
  bulletin: Bulletin;
  photos: BulletinPhoto[];
  missingPhotos: number;
}

/** 编辑：文案 / 照片 / 区块开关。⚠ 三样都不触发 AI（`16` §四之三） */
export const patch = (id: string, body: BulletinPatchInput): Promise<PatchResult> =>
  put<PatchResult>(`/api/bulletins/${encodeURIComponent(id)}`, body);

/** 删除。⚠ 两名管理员互相都能删任意一条 */
export const remove = (id: string): Promise<void> =>
  del<void>(`/api/bulletins/${encodeURIComponent(id)}`);

/** 导出留痕（2026-09-28）。⚠ 导出**成功之后**才调；不动版本号，不会跟正在编辑的人撞冲突 */
export const exported = (id: string, kind: 'png' | 'pdf'): Promise<{ exportedAt: string }> =>
  post<{ exportedAt: string }>(`/api/bulletins/${encodeURIComponent(id)}/export`, { kind });

/**
 * 生成 / 重新生成（**SSE**）。
 *
 * ⚠ 一次生成约 5 次模型调用、几十秒起步，所以后端把真实步骤逐条推过来。
 *   **不抛业务异常**：失败一律走 `error` 事件——流一旦开始就发不了 HTTP 状态码，
 *   两套错误通道会让这里出现两条互不覆盖的分支。这里只在**网络层**真的断了时抛。
 * ⚠ 解析必须**按空行切分并跨 chunk 拼接**（同 `api/assistant.ts`）。
 */
export async function generate(
  periodId: string,
  onEvent: (e: BulletinStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${apiBase()}/api/bulletins/generate`, {
    method: 'POST',
    // ⚠ 会话是 httpOnly Cookie，漏了这行必定 401
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ periodId }),
    signal,
  });
  if (!res.ok || !res.body) throw new Error(`快报生成服务返回 ${res.status}`);

  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i = buf.indexOf('\n\n');
    while (i >= 0) {
      const raw = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const line = raw.split('\n').find((l) => l.startsWith('data:'));
      if (line) {
        try {
          onEvent(JSON.parse(line.slice(5).trim()) as BulletinStreamEvent);
        } catch {
          /* 单条解析失败不中断整条流 */
        }
      }
      i = buf.indexOf('\n\n');
    }
  }
}
